import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar } from '@clareefai/ui';
import { Can } from '../../contexts/AbilityContext';
import PageTitle from '../../components/shared/PageTitle';
import UploadPromoterVideoModal from '../../components/testimonials/UploadPromoterVideoModal';
import { useGetVideoTestimonialsQuery, useGetTestimonialRequestsQuery } from '../../store/vendorApi';
import { useGetMyAssignedPromotersQuery, useGetTeamMembersQuery } from '../../store/teamMemberApi';
import { useGetPromotersQuery } from '../../store/promoterApi';
import { useAuth } from '../../contexts/AuthContext';
import {
  Video, Download, Plus, Search, Play, Send, Grid, List, UploadCloud
} from 'lucide-react';

// DEV-87 — CSM defaults to their own promoters, Sales rep defaults to all.
// Toggle visible to both so they can override either way.
const CSM_DEFAULT_MINE_ROLES = new Set(['team_customer_success', 'team_account_manager']);

const Testimonials: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, isTeamMember } = useAuth();
  const [activeTab, setActiveTab] = useState<'our_requests' | 'promoter_submissions'>('promoter_submissions');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [submissionsViewMode, setSubmissionsViewMode] = useState<'grid' | 'list'>('grid');
  const [showUploadModal, setShowUploadModal] = useState(false);
  // DEV-87 — default based on role. CSM → "mine", others → "all".
  const [scope, setScope] = useState<'all' | 'mine'>(
    user?.role && CSM_DEFAULT_MINE_ROLES.has(user.role) ? 'mine' : 'all'
  );
  const [repFilter, setRepFilter] = useState('');

  // Fetch assigned promoter IDs (only for team members; vendors always see all)
  const { data: assignedPromoters = [] } = useGetMyAssignedPromotersQuery(undefined, { skip: !isTeamMember });
  const { data: teamMembersData = [] } = useGetTeamMembersQuery();
  const { data: promotersData = [] } = useGetPromotersQuery();
  const assignedPromoterIds = useMemo(
    () => new Set(assignedPromoters.map((p: any) => p.id).filter(Boolean)),
    [assignedPromoters]
  );

  // Fetch video testimonials by status
  const { data: completedTestimonials = [], isLoading: loadingCompleted, error: errorCompleted } = useGetVideoTestimonialsQuery({ status: 'COMPLETED' });
  const { data: publishedTestimonials = [], isLoading: loadingPublished, error: errorPublished } = useGetVideoTestimonialsQuery({ status: 'PUBLISHED' });
  const { data: featuredTestimonialsList = [], isLoading: loadingFeatured, error: errorFeatured } = useGetVideoTestimonialsQuery({ status: 'FEATURED' });

  // Combine all testimonials
  const rawPromoterSubmissions = [...completedTestimonials, ...publishedTestimonials, ...featuredTestimonialsList];
  // DEV-87 — scope filter: when "mine", team members see only testimonials from their assigned promoters.
  const allPromoterSubmissions = useMemo(() => {
    if (!isTeamMember || scope === 'all') return rawPromoterSubmissions;
    return rawPromoterSubmissions.filter((t: any) => t.promoter_id && assignedPromoterIds.has(t.promoter_id));
  }, [rawPromoterSubmissions, scope, isTeamMember, assignedPromoterIds]);
  const isLoading = loadingCompleted || loadingPublished || loadingFeatured;

  // Fetch our video testimonial requests from API
  const { data: videoRequests = [] } = useGetTestimonialRequestsQuery({ type: 'VIDEO' });
  const ourTestimonialRequests = useMemo(() => {
    const mapped = videoRequests.map((request) => ({
      id: request.id,
      promoterId: request.promoterId,
      promoterName: request.promoterName,
      promoterCompany: request.promoterCompany || '',
      promoterAvatar: undefined,
      requestDate: request.requestedAt,
      status: request.status === 'VENDOR_REQUESTED' ? 'sent' :
        request.status === 'PROMOTER_APPROVED' ? 'accepted' :
        request.status === 'VENDOR_APPROVED' ? 'approved' :
        request.status === 'REJECTED' ? 'declined' : 'pending',
      emailSent: true,
      reminderCount: 0,
      lastReminder: null,
      product: request.productId || '',
      conversionValue: 0,
      notes: request.rejectionReason || '',
      type: 'our_request' as const
    }));
    // DEV-87 — scope filter: when "mine", team members see only requests for their assigned promoters.
    if (!isTeamMember || scope === 'all') return mapped;
    return mapped.filter((r) => r.promoterId && assignedPromoterIds.has(r.promoterId));
  }, [videoRequests, isTeamMember, scope, assignedPromoterIds]);

  
  // Featured = status FEATURED (from API)
  const featuredTestimonials = featuredTestimonialsList;

  const getSubmissionStatusBadge = (status: string) => {
    const badges = {
      COMPLETED: 'bg-gray-100 text-gray-800',
      PUBLISHED: 'bg-green-100 text-green-800',
      FEATURED: 'bg-purple-100 text-purple-800'
    };

    const labels: Record<string, string> = {
      COMPLETED: t('testimonials.status.completed'),
      PUBLISHED: t('testimonials.status.published'),
      FEATURED: t('testimonials.status.featured')
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      sent: 'bg-blue-100 text-blue-800',
      accepted: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      declined: 'bg-red-100 text-red-800',
      review: 'bg-purple-100 text-purple-800',
      editing: 'bg-orange-100 text-orange-800',
      approved: 'bg-green-100 text-green-800'
    };

    const labels: Record<string, string> = {
      sent: t('testimonials.status.sent'),
      accepted: t('testimonials.status.accepted'),
      pending: t('testimonials.status.pending'),
      declined: t('testimonials.status.declined'),
      review: t('testimonials.status.underReview'),
      editing: t('testimonials.status.editingRequired'),
      approved: t('testimonials.status.approved')
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status as keyof typeof badges]}`}>
        {labels[status]}
      </span>
    );
  };


  const handleTestimonialClick = (testimonial: any) => {
    navigate(`/testimonials/${testimonial.id}`, { state: { testimonial } });
  };

  const handleRequestClick = (request: any) => {
    navigate(`/testimonial-requests/${request.id}`);
  };

  const downloadVideo = async (url: string, filename = 'testimonial.mp4') => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, '_blank');
    }
  };

  // Compact inline stats shown top-right of the page header, switched by the
  // active tab — mirrors the Reviews Management layout for cross-page consistency.
  const renderInlineStats = () => {
    const stats = activeTab === 'our_requests'
      ? [
          { value: ourTestimonialRequests.length, color: 'text-blue-600', label: t('testimonials.ourRequests.statsTotal') },
          { value: ourTestimonialRequests.filter(r => r.status === 'accepted').length, color: 'text-green-600', label: t('testimonials.ourRequests.statsAccepted') },
          { value: ourTestimonialRequests.filter(r => r.status === 'pending').length, color: 'text-yellow-600', label: t('testimonials.ourRequests.statsPending') },
          { value: ourTestimonialRequests.filter(r => r.status === 'declined').length, color: 'text-red-600', label: t('testimonials.ourRequests.statsDeclined') },
        ]
      : [
          { value: allPromoterSubmissions.length, color: 'text-purple-600', label: t('testimonials.submissions.statsTotal') },
          { value: completedTestimonials.length, color: 'text-yellow-600', label: t('testimonials.submissions.statsCompleted') },
          { value: publishedTestimonials.length, color: 'text-green-600', label: t('testimonials.submissions.statsPublished') },
          { value: featuredTestimonialsList.length, color: 'text-purple-600', label: t('testimonials.submissions.statsFeatured') },
        ];
    return (
      <div className="flex items-center gap-5 sm:gap-6">
        {stats.map((s, i) => (
          <div key={i} className="text-center">
            <div className={`text-xl font-bold ${s.color} leading-tight tabular-nums`}>{s.value}</div>
            <div className="text-xs text-gray-500">{s.label}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderOurRequests = () => (
    <div className="space-y-6">
      {/* Our requests management */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('testimonials.ourRequests.heading', { count: ourTestimonialRequests.length })}</h3>
            <p className="text-sm text-gray-600 mt-1">{t('testimonials.ourRequests.subtitle')}</p>
          </div>

          <div className="flex items-center space-x-4">
            {/* DEV-87 — scope toggle (team members only; vendors always see all) */}
            {isTeamMember && (
              <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm">
                <button
                  type="button"
                  onClick={() => setScope('mine')}
                  className={`px-3 py-2 transition-colors ${scope === 'mine' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  {t('testimonials.submissions.myPromoters')}
                </button>
                <button
                  type="button"
                  onClick={() => setScope('all')}
                  className={`px-3 py-2 transition-colors border-l border-gray-300 ${scope === 'all' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  {t('testimonials.submissions.allPromoters')}
                </button>
              </div>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('testimonials.ourRequests.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="all">{t('testimonials.ourRequests.filterAll')}</option>
              <option value="sent">{t('testimonials.ourRequests.filterSent')}</option>
              <option value="accepted">{t('testimonials.ourRequests.filterAccepted')}</option>
              <option value="pending">{t('testimonials.ourRequests.filterPending')}</option>
              <option value="declined">{t('testimonials.ourRequests.filterDeclined')}</option>
            </select>

            <div className="flex items-center space-x-1 border border-gray-300 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title={t('testimonials.ourRequests.gridViewTitle')}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'list'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title={t('testimonials.ourRequests.listViewTitle')}
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            <Can I="create" a="Promoter">
              <button
                onClick={() => navigate('/testimonials/request')}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>{t('testimonials.ourRequests.sendNewRequest')}</span>
              </button>
            </Can>
          </div>
        </div>

        {/* Requests display */}
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ourTestimonialRequests
              .filter(request => {
                const matchesSearch = !searchTerm || 
                  request.promoterName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  request.promoterCompany.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  request.product.toLowerCase().includes(searchTerm.toLowerCase());
                const matchesStatus = filterStatus === 'all' || request.status === filterStatus;
                return matchesSearch && matchesStatus;
              })
              .map((request) => (
              <div
                key={request.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleRequestClick(request)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start space-x-3">
                    <Avatar
                      src={request.promoterAvatar}
                      name={request.promoterName}
                      size="md"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 text-sm truncate">{request.promoterName}</h4>
                      <p className="text-xs text-gray-600 truncate">{request.promoterCompany}</p>
                    </div>
                  </div>
                  {getStatusBadge(request.status)}
                </div>

                <div className="space-y-2 mb-3 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('testimonials.ourRequests.product')}</span>
                    <span className="font-medium text-gray-700 truncate ml-1">{request.product}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('testimonials.ourRequests.requestDate')}</span>
                    <span className="font-medium text-gray-700">{new Date(request.requestDate).toLocaleDateString('en-US')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('testimonials.ourRequests.dealValue')}</span>
                    <span className="font-medium text-green-600">€{request.conversionValue.toLocaleString()}</span>
                  </div>
                  {request.reminderCount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t('testimonials.ourRequests.reminders')}</span>
                      <span className="font-medium text-orange-600">{request.reminderCount}</span>
                    </div>
                  )}
                </div>

                {request.notes && (
                  <div className="bg-gray-50 border border-gray-200 rounded p-2 mb-3">
                    <p className="text-xs text-gray-700 line-clamp-2">{request.notes}</p>
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRequestClick(request);
                    }}
                    className="flex-1 text-blue-600 hover:text-blue-800 text-xs font-medium py-1 px-2 border border-blue-200 rounded hover:bg-blue-50 transition-colors"
                  >
                    {t('common.view')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('testimonials.ourRequests.tableHeaders.promoter')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('testimonials.ourRequests.tableHeaders.product')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('testimonials.ourRequests.tableHeaders.requestDate')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('testimonials.ourRequests.tableHeaders.dealValue')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('testimonials.ourRequests.tableHeaders.status')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('testimonials.ourRequests.tableHeaders.reminders')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('testimonials.ourRequests.tableHeaders.notes')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('testimonials.ourRequests.tableHeaders.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {ourTestimonialRequests
                  .filter(request => {
                    const matchesSearch = !searchTerm || 
                      request.promoterName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      request.promoterCompany.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      request.product.toLowerCase().includes(searchTerm.toLowerCase());
                    const matchesStatus = filterStatus === 'all' || request.status === filterStatus;
                    return matchesSearch && matchesStatus;
                  })
                  .map((request) => (
                  <tr
                    key={request.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => handleRequestClick(request)}
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <Avatar
                          src={request.promoterAvatar}
                          name={request.promoterName}
                          size="md"
                        />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">{request.promoterName}</div>
                          <div className="text-xs text-gray-500">{request.promoterCompany}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900">{request.product}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-700">{new Date(request.requestDate).toLocaleDateString('en-US')}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-green-600">€{request.conversionValue.toLocaleString()}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {getStatusBadge(request.status)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-center">
                        {request.reminderCount > 0 ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            {request.reminderCount}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                        {request.lastReminder && (
                          <div className="text-xs text-gray-500 mt-1">
                            {t('testimonials.ourRequests.lastReminder', { date: new Date(request.lastReminder).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) })}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="max-w-xs">
                        {request.notes ? (
                          <p className="text-sm text-gray-700 line-clamp-2">{request.notes}</p>
                        ) : (
                          <span className="text-xs text-gray-400">{t('testimonials.ourRequests.noNotes')}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRequestClick(request);
                        }}
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium py-1 px-3 border border-blue-200 rounded hover:bg-blue-50 transition-colors"
                      >
                        {t('common.view')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderPromoterSubmissions = () => (
    <div className="space-y-6">
      {/* Promoter submissions management */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('testimonials.submissions.heading', { count: allPromoterSubmissions.length })}</h3>
            <p className="text-sm text-gray-600 mt-1">{t('testimonials.submissions.subtitle')}</p>
          </div>

          <div className="flex items-center space-x-4">
            {/* DEV-87 — scope toggle (team members only; vendors always see all) */}
            {isTeamMember && (
              <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm">
                <button
                  type="button"
                  onClick={() => setScope('mine')}
                  className={`px-3 py-2 transition-colors ${scope === 'mine' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  {t('testimonials.submissions.myPromoters')}
                </button>
                <button
                  type="button"
                  onClick={() => setScope('all')}
                  className={`px-3 py-2 transition-colors border-l border-gray-300 ${scope === 'all' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  {t('testimonials.submissions.allPromoters')}
                </button>
              </div>
            )}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
            >
              <option value="all">{t('testimonials.submissions.filterAll')}</option>
              <option value="COMPLETED">{t('testimonials.submissions.filterCompleted')}</option>
              <option value="PUBLISHED">{t('testimonials.submissions.filterPublished')}</option>
              <option value="FEATURED">{t('testimonials.submissions.filterFeatured')}</option>
            </select>

            <select
              value={repFilter}
              onChange={(e) => setRepFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
            >
              <option value="">{t('testimonials.submissions.allReps', 'All reps')}</option>
              {teamMembersData.map(m => (
                <option key={m.id} value={m.id}>{m.firstName} {m.lastName}</option>
              ))}
            </select>

            <div className="flex items-center space-x-1 border border-gray-300 rounded-lg p-1">
              <button
                onClick={() => setSubmissionsViewMode('grid')}
                className={`p-2 rounded transition-colors ${
                  submissionsViewMode === 'grid'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title={t('testimonials.submissions.gridViewTitle')}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setSubmissionsViewMode('list')}
                className={`p-2 rounded transition-colors ${
                  submissionsViewMode === 'list'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title={t('testimonials.submissions.listViewTitle')}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Submissions display */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-3 text-gray-600">{t('testimonials.submissions.loading')}</span>
          </div>
        ) : allPromoterSubmissions.filter(s => {
          if (filterStatus !== 'all' && s.status !== filterStatus) return false;
          if (repFilter) {
            const p = promotersData.find(p => p.id === (s as any).promoter_id || p.id === (s as any).promoterId);
            if (p?.assignedTeamMember?.id !== repFilter) return false;
          }
          return true;
        }).length === 0 ? (
          <div className="text-center py-12">
            <Video className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">{t('testimonials.submissions.empty')}</p>
          </div>
        ) : submissionsViewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {allPromoterSubmissions
              .filter(s => {
                if (filterStatus !== 'all' && s.status !== filterStatus) return false;
                if (repFilter) {
                  const p = promotersData.find(p => p.id === (s as any).promoter_id || p.id === (s as any).promoterId);
                  if (p?.assignedTeamMember?.id !== repFilter) return false;
                }
                return true;
              })
              .map((submission) => (
              <div
                key={submission.id}
                className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleTestimonialClick(submission)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white font-semibold">
                      {submission.promoterName.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900">{submission.promoterName}</h4>
                      <p className="text-sm text-gray-600">{submission.promoterCompany || t('testimonials.submissions.noCompany')}</p>
                    </div>
                  </div>

                  <div className="flex flex-col items-end space-y-1">
                    {getSubmissionStatusBadge(submission.status)}
                    {submission.isFeatured && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        {t('testimonials.submissions.featuredBadge')}
                      </span>
                    )}
                  </div>
                </div>

                {/* Video Title */}
                {submission.videoTitle && (
                  <div className="mb-3">
                    <h5 className="font-semibold text-gray-900 text-base line-clamp-1">
                      {submission.videoTitle}
                    </h5>
                  </div>
                )}

                {/* Video Description */}
                {submission.videoDescription && (
                  <div className="mb-3">
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {submission.videoDescription}
                    </p>
                  </div>
                )}

                {/* Video Tags */}
                {submission.videoTags && submission.videoTags.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-1.5">
                    {submission.videoTags.slice(0, 3).map((tag, idx) => (
                      <span key={idx} className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium">
                        {tag}
                      </span>
                    ))}
                    {submission.videoTags.length > 3 && (
                      <span className="text-xs text-gray-500 self-center">+{submission.videoTags.length - 3}</span>
                    )}
                  </div>
                )}

                {/* Video Preview */}
                {submission.videoUrl && (
                  <div 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTestimonialClick(submission);
                    }}
                    className="mb-4 relative w-full h-40 bg-gray-900 rounded-lg overflow-hidden cursor-pointer group"
                  >
                    <video
                      src={`${submission.videoUrl}#t=0.1`}
                      preload="metadata"
                      muted
                      playsInline
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-900 to-indigo-800 opacity-60 group-hover:opacity-40 transition-opacity"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <Play className="w-5 h-5 text-purple-600 ml-0.5" />
                      </div>
                    </div>
                    <div className="absolute bottom-2 left-2 text-white text-xs font-medium flex items-center">
                      <Video className="w-3 h-3 mr-1" />
                      {t('testimonials.submissions.video')}
                    </div>
                  </div>
                )}

                <div className="space-y-2 mb-4 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('testimonials.submissions.product')}</span>
                    <span className="font-medium text-gray-700">{submission.productId || t('testimonials.submissions.naShort')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('testimonials.submissions.videoLabel')}</span>
                    <span className="font-medium text-gray-700">{submission.videoUrl ? t('testimonials.submissions.videoAvailable') : t('testimonials.submissions.videoNone')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('testimonials.submissions.submitted')}</span>
                    <span className="font-medium text-gray-700">{new Date(submission.createdAt).toLocaleDateString('en-US')}</span>
                  </div>
                </div>

                {submission.rejectionReason && (
                  <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
                    <p className="text-xs font-medium text-red-900 mb-1">{t('testimonials.submissions.rejectionReason')}</p>
                    <p className="text-xs text-red-700">{submission.rejectionReason}</p>
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTestimonialClick(submission);
                    }}
                    className="flex-1 text-purple-600 hover:text-purple-800 text-sm font-medium py-2 px-3 border border-purple-200 rounded hover:bg-purple-50 transition-colors flex items-center justify-center space-x-1"
                  >
                    <Play className="w-4 h-4" />
                    <span>{t('testimonials.submissions.review')}</span>
                  </button>
                  {submission.videoUrl && (
                    <button
                      onClick={(e) => { e.stopPropagation(); downloadVideo(submission.videoUrl!, `${submission.promoterName}-testimonial.mp4`); }}
                      className="p-2 text-gray-600 hover:text-gray-800 border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                      title={t('testimonials.submissions.downloadVideoTitle')}
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('testimonials.submissions.tableHeaders.promoter')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('testimonials.submissions.tableHeaders.title')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('testimonials.submissions.tableHeaders.description')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('testimonials.submissions.tableHeaders.tags')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('testimonials.submissions.tableHeaders.product')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('testimonials.submissions.tableHeaders.submitted')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('testimonials.submissions.tableHeaders.status')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('testimonials.submissions.tableHeaders.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {allPromoterSubmissions
                  .filter(s => {
                if (filterStatus !== 'all' && s.status !== filterStatus) return false;
                if (repFilter) {
                  const p = promotersData.find(p => p.id === (s as any).promoter_id || p.id === (s as any).promoterId);
                  if (p?.assignedTeamMember?.id !== repFilter) return false;
                }
                return true;
              })
                  .map((submission) => (
                  <tr
                    key={submission.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => handleTestimonialClick(submission)}
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                          {submission.promoterName.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 text-sm">{submission.promoterName}</div>
                          <div className="text-xs text-gray-500">{submission.promoterCompany || t('testimonials.submissions.noCompany')}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-gray-900 max-w-xs">
                        {submission.videoTitle ? (
                          <span className="line-clamp-1">{submission.videoTitle}</span>
                        ) : (
                          <span className="text-gray-400">{t('testimonials.submissions.noTitle')}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-600 max-w-xs">
                        {submission.videoDescription ? (
                          <span className="line-clamp-1">{submission.videoDescription}</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {submission.videoTags && submission.videoTags.length > 0 ? (
                          <>
                            {submission.videoTags.slice(0, 2).map((tag, idx) => (
                              <span key={idx} className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs">
                                {tag}
                              </span>
                            ))}
                            {submission.videoTags.length > 2 && (
                              <span className="text-xs text-gray-500">+{submission.videoTags.length - 2}</span>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900">{submission.productId || t('testimonials.submissions.naShort')}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-700">{new Date(submission.createdAt).toLocaleDateString('en-US')}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex flex-col items-start space-y-1">
                        {getSubmissionStatusBadge(submission.status)}
                        {submission.isFeatured && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            {t('testimonials.submissions.featuredBadge')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTestimonialClick(submission);
                          }}
                          className="text-purple-600 hover:text-purple-800 text-xs font-medium py-1 px-3 border border-purple-200 rounded hover:bg-purple-50 transition-colors flex items-center space-x-1"
                        >
                          <Play className="w-4 h-4" />
                          <span>{t('testimonials.submissions.review')}</span>
                        </button>
                        {submission.videoUrl && (
                          <button
                            onClick={(e) => { e.stopPropagation(); downloadVideo(submission.videoUrl!, `${submission.promoterName}-testimonial.mp4`); }}
                            className="p-1.5 text-gray-600 hover:text-gray-800 border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                            title={t('testimonials.submissions.downloadVideoTitle')}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <PageTitle title={t('testimonials.pageTitle')} />
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('testimonials.heading')}</h1>
          <p className="text-gray-600 mt-1">{t('testimonials.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
          {renderInlineStats()}
          <Can I="create" a="Promoter">
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors active:scale-[0.96]"
            >
              <UploadCloud className="w-4 h-4" />
              <span>{t('testimonials.uploadVideo.button')}</span>
            </button>
          </Can>
        </div>
      </div>

      <UploadPromoterVideoModal isOpen={showUploadModal} onClose={() => setShowUploadModal(false)} />

      {/* Navigation tabs */}
      <nav className="flex space-x-1 bg-gray-200 rounded-xl p-1 mb-6">
        <button
          onClick={() => setActiveTab('our_requests')}
          className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
            activeTab === 'our_requests'
              ? 'bg-blue-600 text-white shadow-md'
              : 'text-gray-600 hover:text-blue-700 hover:bg-white hover:bg-opacity-50'
          }`}
        >
          <Send className="w-4 h-4" />
          <span>{t('testimonials.tabs.ourRequests', { count: ourTestimonialRequests.length })}</span>
        </button>

        <button
          onClick={() => setActiveTab('promoter_submissions')}
          className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
            activeTab === 'promoter_submissions'
              ? 'bg-purple-600 text-white shadow-md'
              : 'text-gray-600 hover:text-purple-700 hover:bg-white hover:bg-opacity-50'
          }`}
        >
          <Video className="w-4 h-4" />
          <span>{t('testimonials.tabs.promoterSubmissions', { count: allPromoterSubmissions.length })}</span>
        </button>
      </nav>

      <div>
        {activeTab === 'our_requests' && renderOurRequests()}
        {activeTab === 'promoter_submissions' && renderPromoterSubmissions()}
      </div>
    </div>
  );
};

export default Testimonials;